/**
 * Cognito refresh-token rotation.
 *
 * Without this, `pomogrow_auth.expiresAt` (launch-time + 3600s) silently
 * downgrades the user to "guest" after one hour — trial banner disappears,
 * purchase prompts for sign-in again.
 *
 * The scheduler:
 * - Refreshes ~5 minutes before expiry via Cognito REFRESH_TOKEN_AUTH.
 * - Re-checks expiry on tab focus / visibility change (setTimeout pauses
 *   when the tab is backgrounded on mobile Safari).
 * - Calls signOut() if Cognito rejects the refresh token (e.g. revoked,
 *   user deleted, 30-day expiry hit) so the UI stops showing stale state.
 * - Does nothing when there is no signed-in user or no stored refreshToken.
 */
import { peekStoredAuth, signOut } from './nativeAppleSignIn';

const COGNITO_REGION = process.env.REACT_APP_COGNITO_REGION || 'eu-west-2';
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '1uuv0tm7i1tpqj7d9k2fmk9cg4';
const COGNITO_ENDPOINT = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;

const AUTH_STORAGE_KEY = 'pomogrow_auth';
const REFRESH_LEAD_MS = 5 * 60 * 1000; // refresh 5 min before expiry

function decodeJwtPayload(jwt) {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Exchange the stored refreshToken for fresh idToken + accessToken.
 * Writes the new tokens to localStorage and dispatches `pomogrow:auth-change`.
 * Returns the new auth record on success, null on failure (caller should
 * treat null as a sign-out and not retry).
 */
export async function refreshIdToken() {
  const auth = peekStoredAuth();
  if (!auth?.refreshToken) return null;

  const res = await fetch(COGNITO_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: COGNITO_CLIENT_ID,
      AuthParameters: { REFRESH_TOKEN: auth.refreshToken },
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const code = (body.__type || '').split('#').pop();
    // 'NotAuthorizedException' = refresh token revoked or expired.
    // Sign the user out so SubscriptionContext drops stale entitlement.
    if (code === 'NotAuthorizedException') signOut();
    return null;
  }

  const data = await res.json();
  const tokens = data.AuthenticationResult;
  if (!tokens?.IdToken) return null;

  const payload = decodeJwtPayload(tokens.IdToken) || {};
  const next = {
    ...auth,
    userId: payload.sub || auth.userId,
    email: payload.email || auth.email,
    name: payload.name || auth.name,
    idToken: tokens.IdToken,
    accessToken: tokens.AccessToken || auth.accessToken,
    // Cognito does NOT issue a new refresh token in this flow — keep the old one.
    expiresAt: Date.now() + (tokens.ExpiresIn || 3600) * 1000,
  };

  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event('pomogrow:auth-change'));
  return next;
}

/**
 * Boot the refresh scheduler. Idempotent — calling more than once is safe;
 * the prior timer/listeners are torn down first. Returns a cleanup fn.
 */
export function startAuthRefreshScheduler() {
  let timer = null;
  let stopped = false;
  let refreshing = false;

  const clearTimer = () => {
    if (timer) { clearTimeout(timer); timer = null; }
  };

  const tick = async () => {
    if (stopped || refreshing) return;
    const auth = peekStoredAuth();
    clearTimer();
    if (!auth?.refreshToken || !auth?.expiresAt) return;

    const msUntilDue = auth.expiresAt - Date.now() - REFRESH_LEAD_MS;
    if (msUntilDue > 0) {
      // Not yet due — schedule a refresh just before expiry.
      timer = setTimeout(tick, msUntilDue);
      return;
    }

    refreshing = true;
    try {
      const next = await refreshIdToken();
      if (stopped) return;
      if (next) {
        // Reschedule for the next cycle.
        const nextDue = next.expiresAt - Date.now() - REFRESH_LEAD_MS;
        timer = setTimeout(tick, Math.max(nextDue, 1000));
      }
      // If next === null, refresh failed and signOut() (or a no-op for
      // network errors) already ran. No timer scheduled; the next auth-change
      // event will restart the scheduler if the user signs in again.
    } catch {
      // Network error or similar — retry in 60s rather than giving up.
      if (!stopped) timer = setTimeout(tick, 60 * 1000);
    } finally {
      refreshing = false;
    }
  };

  const onAuthChange = () => { if (!stopped) tick(); };
  const onFocus = () => { if (!stopped) tick(); };
  const onVisibility = () => { if (!stopped && document.visibilityState === 'visible') tick(); };

  window.addEventListener('pomogrow:auth-change', onAuthChange);
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisibility);

  tick();

  return () => {
    stopped = true;
    clearTimer();
    window.removeEventListener('pomogrow:auth-change', onAuthChange);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
